import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { procApi } from '../../api/procurement.js';
import { wfApi } from '../../api/workflow.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

export default function PurchaseRequests() {
  const { id } = useParams();
  if (id === 'new') return <NewPR />;
  if (id) return <PRDetail id={id} />;
  return <PRList />;
}

function PRList() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState(can('purchase_request', 'list') ? 'all' : 'mine');

  function refresh() {
    (scope === 'mine' ? procApi.myPRs() : procApi.listPRs()).then((d) => setItems(d?.items || []));
  }
  useEffect(refresh, [scope]);

  return (
    <section>
      <header className="page-header">
        <h1>Purchase Requests</h1>
        <div className="actions">
          {can('purchase_request', 'list') && (
            <div className="seg">
              <button className={scope === 'mine' ? 'active' : ''} onClick={() => setScope('mine')}>Mine</button>
              <button className={scope === 'all' ? 'active' : ''} onClick={() => setScope('all')}>All</button>
            </div>
          )}
          <Link to="/procurement/requests/new" className="btn primary">+ New PR</Link>
        </div>
      </header>
      <div className="card">
        <table className="data-table">
          <thead><tr><th>PR #</th><th>Requester</th><th>Department</th><th>Total</th><th>Status</th><th>Submitted</th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td><Link to={`/procurement/requests/${p.id}`}><code>{p.request_no}</code></Link></td>
                <td>{p.requester_name}</td>
                <td className="muted">{p.department_name || '—'}</td>
                <td>{p.currency} {Number(p.total_estimate).toFixed(2)}</td>
                <td><span className={`chip status-${chipStatus(p.status)}`}>{p.status}</span></td>
                <td className="muted">{p.submitted_at?.slice(0, 16).replace('T', ' ')}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="muted">No purchase requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function chipStatus(s) {
  if (s === 'pending') return 'pending';
  if (s === 'approved') return 'approved';
  if (s === 'rejected') return 'rejected';
  if (s === 'cancelled') return 'cancelled';
  if (s === 'rfq_issued' || s === 'po_created' || s === 'closed') return 'completed';
  return 'pending';
}

function PRDetail({ id }) {
  const [pr, setPr] = useState(null);
  const [instance, setInstance] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    procApi.getPR(id).then((d) => setPr(d?.pr)).catch((e) => setError(e.response?.data?.message));
  }, [id]);

  useEffect(() => {
    if (pr?.workflow_instance_id) wfApi.getInstance(pr.workflow_instance_id).then((d) => setInstance(d?.instance)).catch(() => {});
  }, [pr?.workflow_instance_id]);

  if (error) return <div className="alert error">{error}</div>;
  if (!pr) return <div className="muted">Loading…</div>;
  const items = typeof pr.items === 'string' ? JSON.parse(pr.items) : pr.items || [];

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>{pr.request_no}</h1>
          <p className="muted">
            {pr.requester_name} · {pr.department_name || '—'} ·{' '}
            <span className={`chip status-${chipStatus(pr.status)}`}>{pr.status}</span>
          </p>
        </div>
        <Link to="/procurement/requests" className="btn ghost">← All PRs</Link>
      </header>

      <div className="card">
        <h3>Items</h3>
        <table className="data-table">
          <thead><tr><th>Description</th><th>Qty</th><th>Unit</th><th>Est. Unit Price</th><th>Est. Total</th></tr></thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td>{it.description}</td>
                <td>{it.quantity}</td>
                <td>{it.unit}</td>
                <td>{Number(it.estUnitPrice || it.est_unit_price || 0).toFixed(2)}</td>
                <td>{(Number(it.quantity) * Number(it.estUnitPrice || it.est_unit_price || 0)).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={4}><strong>Total estimate</strong></td><td><strong>{pr.currency} {Number(pr.total_estimate).toFixed(2)}</strong></td></tr></tfoot>
        </table>
        {pr.justification && (<><h3>Justification</h3><p>{pr.justification}</p></>)}
      </div>

      {instance && (
        <div className="card">
          <h3>Approval Progress</h3>
          <ol className="timeline">
            {instance.steps.map((s) => (
              <li key={s.id} className={`tl-${s.status}`}>
                <div className="tl-dot" />
                <div className="tl-content">
                  <strong>{s.step_name || s.step_order}</strong>
                  <span className={`chip status-${s.status === 'pending' ? 'pending' : s.status}`}>{s.status}</span>
                  {s.assignee_name && <span className="muted small">→ {s.assignee_name}</span>}
                  {s.acted_at && <div className="muted small">{new Date(s.acted_at).toLocaleString()}</div>}
                  {s.comments && <div className="muted small">"{s.comments}"</div>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}

function NewPR() {
  const nav = useNavigate();
  const [items, setItems] = useState([{ description: '', quantity: 1, unit: 'EA', estUnitPrice: 0 }]);
  const [products, setProducts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState('');
  const [justification, setJustification] = useState('');
  const [priority, setPriority] = useState('normal');
  const [neededBy, setNeededBy] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    procApi.products({ active: true }).then((d) => setProducts(d?.items || []));
    fetch((import.meta.env.VITE_API_BASE_URL || '/api') + '/departments', {
      headers: { Authorization: `Bearer ${localStorage.getItem('bos.access')}` },
    })
      .then((r) => r.json())
      .then((j) => setDepartments(j?.data?.items || []))
      .catch(() => {});
  }, []);

  const total = useMemo(
    () => items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.estUnitPrice || 0), 0),
    [items]
  );

  function setLine(i, k, v) { setItems((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l))); }
  function addLine() { setItems((ls) => [...ls, { description: '', quantity: 1, unit: 'EA', estUnitPrice: 0 }]); }
  function removeLine(i) { setItems((ls) => ls.filter((_, j) => j !== i)); }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        items: items.map((it) => ({
          productId: it.productId || null,
          description: it.description,
          quantity: Number(it.quantity),
          unit: it.unit,
          estUnitPrice: Number(it.estUnitPrice),
        })),
        justification: justification || null,
        priority,
        neededBy: neededBy || null,
        departmentId: departmentId || null,
      };
      const created = await procApi.createPR(payload);
      nav(`/procurement/requests/${created.pr.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  return (
    <section>
      <header className="page-header"><h1>New Purchase Request</h1></header>
      {error && <div className="alert error">{error}</div>}
      <form onSubmit={submit} className="card form-card">
        <div className="grid-form">
          <label>Department
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">—</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label>Priority
            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </label>
          <label>Needed by<input type="date" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} /></label>
          <label className="full">Justification<textarea rows={2} value={justification} onChange={(e) => setJustification(e.target.value)} /></label>
        </div>

        <table className="data-table je-grid">
          <thead><tr><th>Description</th><th>Product</th><th>Qty</th><th>Unit</th><th>Est. Price</th><th>Total</th><th></th></tr></thead>
          <tbody>
            {items.map((l, i) => (
              <tr key={i}>
                <td><input value={l.description} onChange={(e) => setLine(i, 'description', e.target.value)} required /></td>
                <td>
                  <select value={l.productId || ''} onChange={(e) => setLine(i, 'productId', e.target.value || null)}>
                    <option value="">—</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
                  </select>
                </td>
                <td><input type="number" step="0.01" value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} /></td>
                <td><input value={l.unit} onChange={(e) => setLine(i, 'unit', e.target.value)} /></td>
                <td><input type="number" step="0.01" value={l.estUnitPrice} onChange={(e) => setLine(i, 'estUnitPrice', e.target.value)} /></td>
                <td className="num">{(Number(l.quantity) * Number(l.estUnitPrice)).toFixed(2)}</td>
                <td>{items.length > 1 && <button type="button" className="btn ghost small" onClick={() => removeLine(i)}>×</button>}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td><button type="button" className="btn ghost small" onClick={addLine}>+ Add line</button></td>
              <td colSpan={4} className="num"><strong>Total</strong></td>
              <td className="num"><strong>{total.toFixed(2)}</strong></td>
              <td></td>
            </tr>
          </tfoot>
        </table>

        <div className="actions">
          <button type="submit" className="btn primary">Submit PR</button>
        </div>
      </form>
    </section>
  );
}
